import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useApp } from "../context/AppContext";
import { fetchProduct } from "../lib/api";
import { langField } from "../lib/i18n";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang, addToCart, formatPrice } = useApp();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [error, setError] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    fetchProduct(slug).then((p) => {
      setProduct(p);
      if (p.colors?.length) setColor(p.colors[0]);
    });
  }, [slug]);

  if (!product) {
    return (
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20">
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      </div>
    );
  }

  const handleAdd = () => {
    if (product.sizes?.length && !size) {
      setError(t.product.sizes_required);
      return;
    }
    setError("");
    addToCart(product, size || null, color || null);
  };

  return (
    <div
      className="max-w-[1600px] mx-auto px-5 md:px-10 py-10 md:py-16"
      data-testid="page-product-detail"
    >
      <Link to="/shop" className="inline-flex items-center font-mono text-xs tracking-[0.25em] mb-8 kult-link">
        <ArrowLeft size={14} className="mr-2" /> SHOP
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-14">
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] bg-kult-black2 border border-kult-border overflow-hidden">
            <img
              src={product.images[activeImg]}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-main-image"
            />
          </div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border-2 ${
                    activeImg === i ? "border-kult-pink" : "border-kult-border"
                  }`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          {product.badge && (
            <span className="font-mono text-[10px] tracking-[0.3em] bg-kult-yellow text-black px-2 py-1">
              {langField(product, "badge", lang)}
            </span>
          )}
          <h1
            className="font-display text-4xl md:text-5xl leading-tight mt-4"
            data-testid="product-title"
          >
            {langField(product, "name", lang)}
          </h1>
          <p
            className="inline-block font-mono text-2xl bg-kult-yellow text-black px-3 py-1 mt-5"
            data-testid="product-price"
          >
            {formatPrice(product.price)}
          </p>
          <p className="text-kult-muted mt-6 leading-relaxed">
            {langField(product, "description", lang)}
          </p>

          {product.colors?.length > 0 && (
            <div className="mt-8">
              <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-3">
                {t.product.color}
              </p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`px-4 py-2 border-2 font-mono text-xs tracking-widest ${
                      color === c
                        ? "border-kult-pink text-kult-pink"
                        : "border-kult-border text-white hover:border-white"
                    }`}
                    data-testid={`product-color-${c}`}
                  >
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-3">
                {t.product.size}
              </p>
              <div className="grid grid-cols-5 gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setSize(s);
                      setError("");
                    }}
                    className={`py-3 border-2 font-display text-lg ${
                      size === s
                        ? "bg-kult-pink text-black border-kult-pink"
                        : "border-kult-border hover:border-kult-pink"
                    }`}
                    data-testid={`product-size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <p className="mt-4 font-mono text-xs text-kult-pink" data-testid="product-error">
              {error}
            </p>
          )}

          <button
            onClick={handleAdd}
            disabled={!product.in_stock}
            className="w-full mt-8 bg-kult-pink text-black font-display text-2xl py-5 hover:bg-kult-yellow transition-colors disabled:opacity-50 kult-shadow-yellow hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
            data-testid="product-add-to-cart"
          >
            {product.in_stock ? t.product.add_to_cart : t.product.sold_out}
          </button>

          <div className="mt-6 border-t border-kult-border pt-6">
            <p className="font-mono text-xs text-kult-muted leading-relaxed">
              {t.product.shipping}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
